/**
 * DoorAddressForm — Форма адреса для курьерской доставки
 */

import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { cn } from '@/shared/lib/utils';
import type { DoorAddress } from '../model/use-delivery-store';

export type { DoorAddress };

interface DoorAddressFormProps {
  value: DoorAddress;
  onChange: (address: DoorAddress) => void;
  onValidChange?: (isValid: boolean) => void;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const DoorAddressForm = memo(function DoorAddressForm({
  value,
  onChange,
  onValidChange,
  className,
}: DoorAddressFormProps) {
  const [touched, setTouched] = useState({ street: false, house: false });

  const isValid = value.street.trim().length > 0 && value.house.trim().length > 0;

  // Ref чтобы useEffect не перезапускался при каждом пересоздании onValidChange в родителе.
  // Эффект срабатывает ТОЛЬКО когда меняется isValid (а не ссылка на колбэк).
  const onValidChangeRef = useRef(onValidChange);
  onValidChangeRef.current = onValidChange;

  useEffect(() => {
    onValidChangeRef.current?.(isValid);
  }, [isValid]);  // onValidChange намеренно убран из deps

  const handleChange = useCallback((field: keyof DoorAddress, val: string) => {
    onChange({ ...value, [field]: val });
  }, [value, onChange]);

  const handleBlur = useCallback((field: 'street' | 'house') => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const inputClasses = (hasError: boolean) => cn(
    "w-full h-11 px-4 rounded-xl border bg-white dark:bg-dark-card text-ink dark:text-white text-[14px]",
    "placeholder:text-muted-foreground",
    "focus:outline-none focus:ring-2 focus:ring-primary/30",
    "transition-all duration-200",
    hasError 
      ? "border-red-400 dark:border-red-500" 
      : "border-silk/50 dark:border-dark-border"
  );

  return (
    <div className={cn("space-y-3", className)}>
      {/* Street */}
      <div>
        <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
          Улица <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={value.street}
          onChange={(e) => handleChange('street', e.target.value)}
          onBlur={() => handleBlur('street')}
          placeholder="Название улицы"
          className={inputClasses(touched.street && !value.street.trim())}
        />
        {touched.street && !value.street.trim() && (
          <p className="mt-1 text-[11px] text-red-500">Укажите улицу</p>
        )}
      </div>

      {/* House & Building */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
            Дом <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={value.house}
            onChange={(e) => handleChange('house', e.target.value)}
            onBlur={() => handleBlur('house')}
            placeholder="№"
            className={inputClasses(touched.house && !value.house.trim())}
          />
          {touched.house && !value.house.trim() && (
            <p className="mt-1 text-[11px] text-red-500">Укажите дом</p>
          )}
        </div>
        <div>
          <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
            Корпус
          </label>
          <input
            type="text"
            value={value.building || ''}
            onChange={(e) => handleChange('building', e.target.value)}
            placeholder="К/С"
            className={inputClasses(false)}
          />
        </div>
      </div>

      {/* Apartment & Entrance */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
            Квартира
          </label>
          <input
            type="text"
            value={value.apartment || ''}
            onChange={(e) => handleChange('apartment', e.target.value)}
            placeholder="№"
            className={inputClasses(false)}
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
            Подъезд
          </label>
          <input
            type="text"
            value={value.entrance || ''}
            onChange={(e) => handleChange('entrance', e.target.value)}
            placeholder="№"
            className={inputClasses(false)}
          />
        </div>
      </div>

      {/* Floor & Intercom */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
            Этаж
          </label>
          <input
            type="text"
            value={value.floor || ''}
            onChange={(e) => handleChange('floor', e.target.value)}
            placeholder="№"
            className={inputClasses(false)}
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-muted-foreground mb-1.5">
            Домофон
          </label>
          <input
            type="text"
            value={value.intercom || ''}
            onChange={(e) => handleChange('intercom', e.target.value)}
            placeholder="Код"
            className={inputClasses(false)}
          />
        </div>
      </div>
    </div>
  );
});
